/**
 * DrawServiceAreaDialog
 *
 * Opens a Google Map with the Drawing Manager enabled.
 * Admin draws a polygon, fills in country/city/display name, then saves.
 * The polygon is uploaded to S3 via addServiceArea mutation.
 */

import { useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, CircularProgress, Grid,
} from '@mui/material';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { generateClient } from 'aws-amplify/api';

const client = generateClient();
const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

const ADD_SERVICE_AREA = `
  mutation AddServiceArea($input: AddServiceAreaInput!) {
    addServiceArea(input: $input) { country city serviceActive displayName }
  }
`;

// Default center — Monrovia, Liberia
const DEFAULT_CENTER = { lat: 6.3156, lng: -10.8074 };

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: (area: any) => void;
}

/** Inner component — needs to be inside APIProvider to use useMap */
function DrawingMap({ onPolygonComplete }: { onPolygonComplete: (coords: [number, number][]) => void }) {
  const map = useMap();
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);

  const initDrawing = useCallback(() => {
    if (!map || drawingManagerRef.current) return;

    const dm = new google.maps.drawing.DrawingManager({
      drawingMode: google.maps.drawing.OverlayType.POLYGON,
      drawingControl: true,
      drawingControlOptions: {
        position: google.maps.ControlPosition.TOP_CENTER,
        drawingModes: [google.maps.drawing.OverlayType.POLYGON],
      },
      polygonOptions: {
        fillColor: '#1976d2',
        fillOpacity: 0.25,
        strokeColor: '#1976d2',
        strokeWeight: 2,
        editable: true,
        draggable: true,
      },
    });

    dm.setMap(map);
    drawingManagerRef.current = dm;

    google.maps.event.addListener(dm, 'polygoncomplete', (polygon: google.maps.Polygon) => {
      // Remove previous polygon if any
      if (polygonRef.current) polygonRef.current.setMap(null);
      polygonRef.current = polygon;

      // Switch back to hand tool after drawing
      dm.setDrawingMode(null);

      const path = polygon.getPath();
      const coords: [number, number][] = [];
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i);
        coords.push([pt.lng(), pt.lat()]); // GeoJSON: [lng, lat]
      }
      onPolygonComplete(coords);

      // Re-fire when user edits the polygon
      google.maps.event.addListener(path, 'set_at', () => {
        const updated: [number, number][] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const pt = path.getAt(i);
          updated.push([pt.lng(), pt.lat()]);
        }
        onPolygonComplete(updated);
      });
      google.maps.event.addListener(path, 'insert_at', () => {
        const updated: [number, number][] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const pt = path.getAt(i);
          updated.push([pt.lng(), pt.lat()]);
        }
        onPolygonComplete(updated);
      });
    });
  }, [map, onPolygonComplete]);

  // Init drawing manager once map is ready
  if (map && !drawingManagerRef.current) {
    // Load drawing library then init
    const loader = (window as any).google?.maps?.drawing
      ? Promise.resolve()
      : new Promise<void>(resolve => {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=drawing&callback=__gmDrawingReady`;
          (window as any).__gmDrawingReady = resolve;
          document.head.appendChild(script);
        });
    loader.then(initDrawing);
  }

  return null;
}

export default function DrawServiceAreaDialog({ open, onClose, onSaved }: Props) {
  const [coords, setCoords] = useState<[number, number][] | null>(null);
  const [form, setForm] = useState({ country: 'liberia', city: '', displayName: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!coords || coords.length < 3) { setError('Please draw a polygon on the map first.'); return; }
    if (!form.country.trim() || !form.city.trim()) { setError('Country and city are required.'); return; }
    setSaving(true); setError(null);
    try {
      const result: any = await client.graphql({
        query: ADD_SERVICE_AREA,
        variables: {
          input: {
            country: form.country.trim().toLowerCase(),
            city: form.city.trim().toLowerCase(),
            displayName: form.displayName.trim() || `${form.city.trim()}, ${form.country.trim()}`,
            polygonCoordinates: JSON.stringify(coords),
          },
        },
      });
      onSaved(result.data?.addServiceArea);
      handleClose();
    } catch (e: any) {
      setError(e.message ?? 'Failed to save service area');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setCoords(null);
    setForm({ country: 'liberia', city: '', displayName: '' });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Add Service Area</DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ height: 480, position: 'relative' }}>
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['drawing']}>
            <Map
              mapId="libride-draw-area"
              defaultCenter={DEFAULT_CENTER}
              defaultZoom={12}
              style={{ width: '100%', height: '100%' }}
              gestureHandling="greedy"
            >
              <DrawingMap onPolygonComplete={setCoords} />
            </Map>
          </APIProvider>
          {!coords && (
            <Box sx={{
              position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
              bgcolor: 'rgba(0,0,0,0.65)', color: '#fff', px: 2, py: 1, borderRadius: 2,
              pointerEvents: 'none',
            }}>
              <Typography variant="caption">Use the polygon tool in the toolbar to draw your service area</Typography>
            </Box>
          )}
          {coords && (
            <Box sx={{
              position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)',
              bgcolor: 'success.main', color: '#fff', px: 2, py: 1, borderRadius: 2,
              pointerEvents: 'none',
            }}>
              <Typography variant="caption">✓ Polygon drawn ({coords.length} points) — fill in details below and save</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Country" size="small" value={form.country}
                onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
                helperText="e.g. liberia" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="City / Area code" size="small" value={form.city}
                onChange={e => setForm(f => ({ ...f, city: e.target.value }))}
                helperText="e.g. monrovia (used as S3 key)" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Display Name" size="small" value={form.displayName}
                onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))}
                helperText="e.g. Monrovia, Liberia (shown in admin UI)" />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !coords || !form.city.trim()}
        >
          {saving ? <CircularProgress size={20} /> : 'Save Service Area'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
